import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import { AGENT_NAME_SHORT } from '../brand';
import { savedWorkBlocks, savedWorkMarkdown, savedWorkTypeLabel } from '../domain/savedWork';
import './SavedWork.css';

// Saved work — the durable pieces Arc saves for a case (summaries, timelines,
// people lists, key findings). Two entry points onto one modal: the "Saved
// work" button in the Ask Arc toolbar (browse everything), and the card under
// the reply that saved or updated a piece (opens straight to it).

const PRINT_CSS = `
  body { font: 14px/1.5 -apple-system, Segoe UI, Roboto, sans-serif; color: #1a1a1a; max-width: 760px; margin: 32px auto; padding: 0 16px; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 17px; margin: 22px 0 6px; }
  h3 { font-size: 15px; margin: 18px 0 6px; }
  h4 { font-size: 14px; margin: 14px 0 4px; }
  p { margin: 6px 0; white-space: pre-wrap; }
  .sw-sub { color: #666; margin: 0 0 18px; }
  dl { margin: 6px 0; display: grid; grid-template-columns: max-content 1fr; gap: 2px 12px; }
  dt { font-weight: 600; } dd { margin: 0; }
  table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 12px; }
  th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; }
`;

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

function Blocks({ blocks }) {
  return blocks.map((b, i) => {
    if (b.type === 'heading') {
      const Tag = `h${b.level}`;
      return <Tag key={i}>{b.text}</Tag>;
    }
    if (b.type === 'text') return <p key={i}>{b.text}</p>;
    if (b.type === 'fields') {
      return (
        <dl key={i}>
          {b.items.map(([k, v], j) => [<dt key={`k${j}`}>{k}</dt>, <dd key={`v${j}`}>{v}</dd>])}
        </dl>
      );
    }
    if (b.type === 'list') {
      return <ul key={i}>{b.items.map((item, j) => <li key={j}>{item}</li>)}</ul>;
    }
    if (b.type === 'table') {
      return (
        <div key={i} className="sw-table-wrap">
          <table>
            <thead><tr>{b.columns.map((c, j) => <th key={j}>{c}</th>)}</tr></thead>
            <tbody>
              {b.rows.map((r, j) => <tr key={j}>{r.map((cell, k) => <td key={k}>{cell}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  });
}

/**
 * SavedWorkModal — browse and open a case's saved work.
 *
 * Props:
 *   caseId
 *   initialArtifactId   — open straight to this one (the chat card passes it)
 *   onOpenConversation  — (conversationId) => void; when given, the viewer
 *                         offers a jump back to the conversation that saved it
 *   onClose
 */
export default function SavedWorkModal({ caseId, initialArtifactId, onOpenConversation, onClose }) {
  const [items, setItems] = useState(null); // null = loading
  const [listError, setListError] = useState(null);
  const [selectedId, setSelectedId] = useState(initialArtifactId || null);
  const [detail, setDetail] = useState(null); // { id, artifact } | { id, error }
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const docRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`/exploration/case/${caseId}/artifacts`);
        if (cancelled) return;
        const rows = data.artifacts || [];
        setItems(rows);
        setSelectedId(prev => prev || rows[0]?.id || null);
      } catch (err) {
        if (!cancelled) { setItems([]); setListError(err.message || 'Could not load saved work'); }
      }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    setConfirmDelete(false);
    (async () => {
      try {
        const artifact = await api.get(`/exploration/case/${caseId}/artifacts/${selectedId}`);
        if (!cancelled) setDetail({ id: selectedId, artifact });
      } catch (err) {
        if (!cancelled) {
          setDetail({
            id: selectedId,
            error: err.status === 404
              ? 'This saved work is no longer available — it may have been deleted.'
              : (err.message || 'Could not open this saved work'),
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, selectedId]);

  const current = detail && detail.id === selectedId ? detail : null;
  const artifact = current?.artifact || null;
  const blocks = useMemo(() => (artifact ? savedWorkBlocks(artifact.content) : []), [artifact]);
  const subtitle = artifact
    ? `${savedWorkTypeLabel(artifact.artifact_type)} · saved by ${AGENT_NAME_SHORT} ${fmtDate(artifact.updated_at)}`
    : '';
  const markdown = useMemo(
    () => (artifact ? savedWorkMarkdown(blocks, { title: artifact.title, subtitle }) : ''),
    [artifact, blocks, subtitle],
  );

  const copy = async () => {
    const html = docRef.current?.innerHTML || '';
    try {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([markdown], { type: 'text/plain' }),
      })]);
    } catch {
      try { await navigator.clipboard.writeText(markdown); } catch { /* ignore */ }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = (artifact.title || 'saved-work').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
    a.href = url; a.download = `${safeName || 'saved-work'}.md`; a.click();
    URL.revokeObjectURL(url);
  };

  const printIt = () => {
    const html = docRef.current?.innerHTML;
    if (!html) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><style>${PRINT_CSS}</style></head><body>${html}</body></html>`);
    w.document.close();
    // Set after write — the title is free text and must not be parsed as HTML.
    w.document.title = artifact.title || 'Saved work';
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.del(`/exploration/case/${caseId}/artifacts/${selectedId}`);
      const rest = (items || []).filter(a => a.id !== selectedId);
      setItems(rest);
      setDetail(null);
      setSelectedId(rest[0]?.id || null);
    } catch (err) {
      setDetail({ id: selectedId, artifact, error: err.message || 'Delete failed' });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return createPortal((
    <div className="np-export-overlay" onClick={onClose}>
      <div className="np-export-modal sw-modal" onClick={e => e.stopPropagation()}>
        <div className="np-export-head">
          <h3>Saved work</h3>
          <button className="np-icon-btn" onClick={onClose} title="Close">✕</button>
        </div>

        <div className="np-export-body">
          <div className="np-export-pick sw-list">
            {items === null && <div className="np-empty">Loading…</div>}
            {listError && <div className="np-error">{listError}</div>}
            {items && items.length === 0 && !listError && (
              <div className="np-empty">
                Nothing saved yet.
                <div className="np-empty-hint">
                  When {AGENT_NAME_SHORT} saves a summary, timeline, or list for this case, it appears here.
                </div>
              </div>
            )}
            {(items || []).map(a => (
              <button
                key={a.id}
                type="button"
                className={`sw-row${a.id === selectedId ? ' sw-row-active' : ''}`}
                onClick={() => setSelectedId(a.id)}
              >
                <span className="sw-row-title">{a.title || 'Untitled'}</span>
                <span className="sw-row-meta">{savedWorkTypeLabel(a.artifact_type)} · {fmtDate(a.updated_at)}</span>
              </button>
            ))}
          </div>

          <div className="np-export-preview sw-doc">
            {current?.error && <div className="np-error">{current.error}</div>}
            {selectedId && !current && <div className="np-empty">Loading…</div>}
            {artifact && (
              <div ref={docRef}>
                <h1>{artifact.title || 'Untitled'}</h1>
                <p className="sw-sub">{subtitle}</p>
                {blocks.length === 0
                  ? <p>This saved work is empty.</p>
                  : <Blocks blocks={blocks} />}
              </div>
            )}
          </div>
        </div>

        <div className="np-export-foot">
          {artifact && (confirmDelete ? (
            <>
              <span className="sw-confirm">Delete this saved work? This can’t be undone.</span>
              <button className="btn btn-sm sw-danger" onClick={remove} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>Keep</button>
            </>
          ) : (
            <button className="btn btn-sm btn-ghost" onClick={() => setConfirmDelete(true)}>Delete</button>
          ))}
          <span style={{ flex: 1 }} />
          {artifact?.conversation_id && onOpenConversation && (
            <button className="btn" onClick={() => onOpenConversation(artifact.conversation_id)}>
              Open conversation
            </button>
          )}
          <button className="btn" onClick={download} disabled={!artifact}>Download .md</button>
          <button className="btn" onClick={printIt} disabled={!artifact}>Print / PDF</button>
          <button className="np-add-btn" onClick={copy} disabled={!artifact}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

/**
 * SavedWorkCard — typed card for save_artifact / update_artifact results
 * (ChatMessage TOOL_CARDS). Tells the person the reply's work was saved and
 * opens it; without it the save was invisible outside Arc's own memory.
 */
export function SavedWorkCard({ result }) {
  const { caseId } = useParams();
  const [open, setOpen] = useState(false);
  const artifactId = result?.artifact_id;
  if (!caseId || !artifactId) return null;
  const updated = result.status === 'updated';
  return (
    <div className="sw-card">
      <span className="sw-card-icon" aria-hidden="true">📎</span>
      <span className="sw-card-text">
        <b>{updated ? 'Saved work updated' : 'Added to Saved work'}</b>
        {result.title ? ` — ${result.title}` : ''}
      </span>
      <button className="btn btn-sm" onClick={() => setOpen(true)}>Open</button>
      {open && (
        <SavedWorkModal caseId={caseId} initialArtifactId={artifactId} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}
